type OrderBill = { total: number; [key: string]: unknown };

function isOrderBill(value: unknown): value is OrderBill {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof Reflect.get(value, "total") === "number"
  );
}

function isOrderBillArray(value: unknown): value is OrderBill[] {
  return Array.isArray(value) && value.every(isOrderBill);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function readObject(value: unknown): Record<string, unknown> | null {
  return isRecord(value) ? value : null;
}

function readStringField(
  record: Record<string, unknown>,
  key: string
): string | undefined {
  const value = Reflect.get(record, key);
  return typeof value === "string" ? value : undefined;
}

function readNumberField(
  record: Record<string, unknown>,
  key: string
): number | undefined {
  const value = Reflect.get(record, key);
  return typeof value === "number" ? value : undefined;
}

export type OrderPageItem = Record<string, unknown>;

export function billsFromOrders(data: OrderPageItem[]) {
  return data
    .flatMap((item) => {
      const bills = isOrderBillArray(item.bills) ? item.bills : undefined;
      const localId = readNumberField(item, "localId") ?? 0;
      const orderRecord = readObject(item);
      const orderType = orderRecord
        ? readStringField(orderRecord, "type")
        : undefined;
      const createdAt = orderRecord
        ? readNumberField(orderRecord, "createdAt")
        : undefined;
      const location = readObject(item.location);
      const employee = readObject(item.employee);
      const session = readObject(item.session);
      const deviceInfo = session ? readObject(session.deviceInfo) : null;

      return (
        bills?.map((bill, index) => ({
          id: `B-${localId}-${index + 1}`,
          bill,
          order: {
            type: orderType ?? "unknown",
            createdAt: createdAt ?? 0,
          },
          location: location
            ? {
                _id: readStringField(location, "_id") ?? "",
                name: readStringField(location, "name") ?? "",
              }
            : null,
          employee: employee
            ? {
                _id: readStringField(employee, "_id") ?? "",
                firstName: readStringField(employee, "firstName") ?? "",
                lastName: readStringField(employee, "lastName") ?? "",
              }
            : null,
          session: session
            ? {
                deviceId: readStringField(session, "deviceId") ?? "",
                deviceInfo: {
                  model: deviceInfo
                    ? (readStringField(deviceInfo, "model") ?? "")
                    : "",
                  os: deviceInfo
                    ? (readStringField(deviceInfo, "os") ?? "")
                    : "",
                },
              }
            : null,
        })) ?? []
      );
    })
    .filter(Boolean);
}
